import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import VisibilityIcon from "@mui/icons-material/Visibility";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TextView from "../../../TextView";
import ImageModal from "../../../ImageModal";
import { colors } from "../../../../utils";
import { useLocation } from "react-router-dom";
import useScreenSize from "../../../../hooks/useScreenSize";
import MediaSkeleton from "../../../MediaSkeleton";
import MediaFallback from "../MediaFallback";
import CaptionOverlayView from "../CaptionOverlay/CaptionOverlayView";
import useMediaLoadRetry from "./useMediaLoadRetry";
import { resolveCaptionBarColors } from "./captionBarColors";

const ImageContainer = styled.div`
  overflow: hidden;
  position: relative;
  cursor: pointer;
  width: 100%;
  height: 100%;
`;

// T22.138: background + text colour come from resolveCaptionBarColors (border
// colour, else segment colour, auto AA text) via inline style.
const TextContainer = styled.div`
  padding: 8px;
  position: absolute;
  bottom: 0;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const IconsContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  gap: 10px;
  z-index: 1;
  background-color: ${({ showIcons }) =>
    showIcons ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0)"};
  opacity: ${({ showIcons }) => (showIcons ? 1 : 0)};
  visibility: ${({ showIcons }) => (showIcons ? "visible" : "hidden")};
  transition: background-color 0.5s ease-in-out, opacity 0.5s ease-in-out,
    visibility 0.5s ease-in-out;
 
  ${ImageContainer}:hover & {
    opacity: ${({ disableHover, showIcons }) =>
      disableHover && !showIcons ? 0 : 1};
    visibility: ${({ disableHover, showIcons }) =>
      disableHover && !showIcons ? "hidden" : "visible"};
    background-color: ${({ disableHover, showIcons }) =>
      disableHover && !showIcons ? "rgba(0, 0, 0, 0)" : "rgba(0, 0, 0, 0.5)"};
  }
`;

const IconView = styled.div`
  background-color: white;
  display: flex;
  border-radius: 4px;
  padding: 4px 6px;
  cursor: pointer;
  align-items: center;

  &:hover .hidden-text {
    display: ${({ disableHover }) => (disableHover ? "none" : "block")};
  }
`;

const Text = styled(TextView)`
  color: ${colors.purple};
  margin-left: 4px;
  display: none;
`;

const ImageBlock = ({
  src,
  title,
  showImageTitle,
  showHyperlink,
  hyperlink,
  style,
  imageStyle,
  isImageLoading,
  captionOverlay,
  // T22.138: palette-resolved block border colour (used even when the border
  // width is 0) and segment colour; see captionBarColors.js.
  captionBorderColor,
  captionSegmentColor,
  hasImage = true,
  ...rest
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [showIcons, setShowIcons] = useState(false);

  const location = useLocation();
  const isEditPage = location.pathname.includes("builder");
  const isMobileScreen = location.pathname.includes("wheel/segment");
  const { isMobilePortrait, isMobileLandscape } = useScreenSize();
  const iconRef = useRef();

  const disableHover = isMobilePortrait || isMobileLandscape;
  const captionBar = resolveCaptionBarColors({
    borderColor: captionBorderColor,
    segmentColor: captionSegmentColor,
  });

  const isGiphyOrGif =
    src && (src.includes("giphy.com") || src.toLowerCase().includes(".gif"));

  // Load state + bounded retry-then-fallback (QA sheet row 19: a freshly-
  // uploaded image can 404/error on its very first paint — S3/CDN
  // read-after-write propagation lag, measured on staging — even though the
  // exact same URL loads fine moments later). See useMediaLoadRetry for the
  // retry mechanics; extracted so this component stays simple to read.
  const { imgRef, imageLoaded, hasError, uniqueSrc, onImgError } =
    useMediaLoadRetry({ src, isGiphyOrGif });

  const onOpenLink = () => {
    if (showHyperlink && hyperlink) {
      const url =
        hyperlink.startsWith("http://") || hyperlink.startsWith("https://")
          ? hyperlink
          : `https://${hyperlink}`;

      window.open(url, "_blank");
    }
  };

  const onOpenImage = () => {
    setOpenModal(true);
  };

  const toggleIconsVisibility = () => {
    if (disableHover) {
      setShowIcons(!showIcons);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (iconRef.current && !iconRef.current.contains(event.target)) {
        setShowIcons(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [iconRef]);

  // Defensive error state: a broken/missing asset shows a clean placeholder
  // (line-art icon + label) instead of a broken <img>. Inherits the block's
  // styling so it occupies the same footprint the image would have.
  if (hasError) {
    return (
      <ImageContainer style={style}>
        <MediaFallback variant="image" />
      </ImageContainer>
    );
  }

  return (
    <ImageContainer style={style}>
      {/* Display icons for image actions, except in edit mode */}
      {!isEditPage && !isMobileScreen && (
        <IconsContainer
          onClick={toggleIconsVisibility}
          ref={iconRef}
          disableHover={disableHover}
          showIcons={showIcons}
        >
          {/* No real photo selected yet: there is nothing to enlarge, so the
              control must not render (was inviting a click into a modal for
              an image that doesn't exist). */}
          {hasImage && (
            <IconView
              role="button"
              tabIndex={0}
              aria-label="Enlarge image"
              onClick={onOpenImage}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onOpenImage();
                }
              }}
              disableHover={disableHover}
            >
              <VisibilityIcon style={{ color: colors.purple }} />

              {/* <Text className="hidden-text" variant="tag">
                View Image
              </Text> */}
            </IconView>
          )}
          {showHyperlink && (
            <IconView onClick={onOpenLink} disableHover={disableHover}>
              <OpenInNewIcon style={{ color: colors.purple }} />
              {/* <Text className="hidden-text" variant="tag">
                Open Link
              </Text> */}
            </IconView>
          )}
        </IconsContainer>
      )}

      {((isImageLoading && !isGiphyOrGif) ||
        (isGiphyOrGif && !imageLoaded)) && (
        <MediaSkeleton minHeight={100} label="Loading image" />
      )}

      <img
        ref={imgRef}
        key={uniqueSrc} // Use uniqueSrc to force reload if necessary
        src={uniqueSrc}
        alt={title || ""}
        onError={onImgError}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display:
            (isImageLoading && !isGiphyOrGif) || (isGiphyOrGif && !imageLoaded)
              ? "none"
              : "block",
          ...imageStyle,
        }}
        onClick={toggleIconsVisibility}
        {...rest}
      />
      {/* Display image title if applicable */}
      {showImageTitle && title && (
        <TextContainer data-testid="image-caption-bar" style={{ backgroundColor: captionBar.backgroundColor }}>
          <TextView
            variant="largeText"
            style={{ color: captionBar.color, fontWeight: 500 }}
          >
            {title}
          </TextView>
        </TextContainer>
      )}
      {/* IMAGE_CAPTION_OVERLAY (DL-037 / N40): a caption over the image at one of 9
          grid spots, with the AA readable-contrast guard. Renders nothing when
          there is no overlay text, so a plain image is byte-identical. */}
      <CaptionOverlayView overlay={captionOverlay} />
      {/* Modal to view image in full size */}
      {/* QA-54 (Kelly, 2026-07-31): the caption MUST survive enlargement — it is
          the teaching content, not decoration. This was hardcoded showTitle={false},
          so the enlarged view silently dropped a caption the inline image showed.
          Mirror the inline rule exactly (line ~222) so the two can never diverge. */}
      <ImageModal
        showTitle={Boolean(showImageTitle && title)}
        open={openModal}
        handleClose={() => setOpenModal(false)}
        images={[{ src, title }]}
      />
    </ImageContainer>
  );
};

export default ImageBlock;
